// src/components/layout/InsightsSectionLayout.tsx
//
// Insights section chrome (collapsible sidebar + SectionSubNav) wrapped
// around a page's own content — the same composition InsightsHome,
// AnalyticsLanding and DeepDiveReport wire up inline.
//
// The Reports pages use this wrapper rather than repeating the wiring:
// each of them renders several early-return states (loading / blocked /
// error / ready) and every one of those has to keep the section nav.

import { useEffect, useState } from "react";
import { Box } from "@mantine/core";

import ChatLayoutMantine from "@/layout/ChatLayoutMantine";
import SectionSubNav from "./SectionSubNav";
import SidebarShell from "./SidebarShell";

type Props = {
  children: React.ReactNode;
  /**
   * Opt-in "fill" variant: the content area does not scroll and instead
   * hands its full height to the child, which becomes the scroll owner.
   *
   * The default wrapper scrolls (`overflow: auto`), which is right for
   * every stacked-content page in Insights. It is wrong for the report
   * reading room (`ReportView`), where the sandbox iframe is the only
   * scroller: a second scroll owner one level up produced nested
   * scrollbars and forced the iframe's height to be computed from a
   * hardcoded chrome tally. `fill` makes the single-scroller structural
   * rather than arithmetic.
   *
   * Opt-in per route on purpose — flipping the default would silently
   * clip every other Insights page.
   */
  fill?: boolean;
};

export default function InsightsSectionLayout({ children, fill = false }: Props) {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("insights-sidebar-collapsed") === "true",
  );

  useEffect(() => {
    const handler = () =>
      setCollapsed(localStorage.getItem("insights-sidebar-collapsed") === "true");
    window.addEventListener("insights-sidebar-toggle", handler);
    return () => window.removeEventListener("insights-sidebar-toggle", handler);
  }, []);

  return (
    <ChatLayoutMantine
      sidebarType="insights"
      sidebar={
        <SidebarShell
          collapsed={collapsed}
          sidebarType="insights"
          sectionNav={<SectionSubNav section="insights" collapsed={collapsed} />}
        />
      }
    >
      <Box
        style={{
          flex: 1,
          minHeight: 0,
          overflow: fill ? "hidden" : "auto",
          display: fill ? "flex" : undefined,
          flexDirection: fill ? "column" : undefined,
        }}
      >
        {children}
      </Box>
    </ChatLayoutMantine>
  );
}
